// Budowanie i parsowanie prostych wyrażeń cron dla przyjaznego selecta harmonogramu.
// Wspieramy tylko kilka typowych wzorców ("co X minut", "co godzinę", "codziennie",
// "co tydzień", "co miesiąc") - wszystko inne (zakresy, listy, wyrażenia naturalne
// typu "every day at 3am") zostaje w trybie zaawansowanym jako surowy tekst.

export const MODE_MINUTES = "minutes"
export const MODE_HOURLY = "hourly"
export const MODE_DAILY = "daily"
export const MODE_WEEKLY = "weekly"
export const MODE_MONTHLY = "monthly"
export const MODE_ADVANCED = "advanced"

// Scheduler kont chodzi co 5 minut (config/solid/recurring_*.yml), więc nie oferujemy
// gęstszej granulacji niż 15 minut - poniżej harmonogram byłby tylko pozorny
// lista musi być zsynchronizowana z CRON_MINUTE_INTERVALS w app/helpers/cron_schedule_helper.rb
export const MINUTE_INTERVALS = [15, 30]

const DEFAULTS = { interval: 30, minute: 0, hour: 8, weekday: 1, monthday: 1 }

// Składa wyrażenie cron z pól prostego trybu. Zwraca null dla trybu zaawansowanego
// (tam wartość bierzemy wprost z inputa) albo dla trybu nieznanego.
export function buildCron(state) {
  const s = { ...DEFAULTS, ...(state || {}) }
  const minute = clamp(s.minute, 0, 59)
  const hour = clamp(s.hour, 0, 23)

  switch (s.mode) {
    case MODE_MINUTES: {
      const interval = MINUTE_INTERVALS.includes(Number(s.interval)) ? Number(s.interval) : DEFAULTS.interval
      return `*/${interval} * * * *`
    }
    case MODE_HOURLY:
      return `${minute} * * * *`
    case MODE_DAILY:
      return `${minute} ${hour} * * *`
    case MODE_WEEKLY:
      return `${minute} ${hour} * * ${clamp(s.weekday, 0, 6)}`
    case MODE_MONTHLY:
      return `${minute} ${hour} ${clamp(s.monthday, 1, 31)} * *`
    default:
      return null
  }
}

// Rozpoznaje wyrażenie cron i zwraca stan pól prostego trybu. Wyrażenie, którego nie
// da się przedstawić w prostych polach (zakresy, listy, strefa czasowa, zapis naturalny),
// wraca jako tryb zaawansowany - dzięki temu edycja nie psuje istniejącej wartości.
export function parseCron(expression) {
  const raw = (expression || "").trim()
  const advanced = { mode: MODE_ADVANCED, ...DEFAULTS, raw }
  // nowa reguła (pusta wartość) startuje w najczęstszym trybie "codziennie" o domyślnej
  // godzinie - user od razu widzi sensowny harmonogram zamiast pustego pola crona
  if (raw === "") return { mode: MODE_DAILY, ...DEFAULTS, raw: "" }

  const parts = raw.split(/\s+/)
  if (parts.length !== 5) return advanced

  const [minute, hour, monthday, month, weekday] = parts
  if (month !== "*") return advanced

  const stepMatch = minute.match(/^\*\/(\d{1,2})$/)
  if (stepMatch && hour === "*" && monthday === "*" && weekday === "*") {
    const interval = Number(stepMatch[1])
    if (!MINUTE_INTERVALS.includes(interval)) return advanced
    return { mode: MODE_MINUTES, ...DEFAULTS, interval, raw }
  }

  if (!isNumber(minute, 0, 59)) return advanced
  const minuteValue = Number(minute)

  if (hour === "*" && monthday === "*" && weekday === "*") {
    return { mode: MODE_HOURLY, ...DEFAULTS, minute: minuteValue, raw }
  }

  if (!isNumber(hour, 0, 23)) return advanced
  const hourValue = Number(hour)

  if (monthday === "*" && weekday === "*") {
    return { mode: MODE_DAILY, ...DEFAULTS, minute: minuteValue, hour: hourValue, raw }
  }

  // cron (Fugit) traktuje 7 jak niedzielę - normalizujemy do 0, żeby tryb tygodniowy je rozpoznał
  const weekdayNorm = weekday === "7" ? "0" : weekday
  if (monthday === "*" && isNumber(weekdayNorm, 0, 6)) {
    return { mode: MODE_WEEKLY, ...DEFAULTS, minute: minuteValue, hour: hourValue, weekday: Number(weekdayNorm), raw }
  }

  if (weekday === "*" && isNumber(monthday, 1, 31)) {
    return { mode: MODE_MONTHLY, ...DEFAULTS, minute: minuteValue, hour: hourValue, monthday: Number(monthday), raw }
  }

  return advanced
}

// Opis harmonogramu dla usera - szablony tłumaczeń przychodzą z widoku (data-value),
// tutaj tylko podstawiamy wartości. Brak szablonu = brak opisu (pokazujemy samo wyrażenie).
export function describeCron(state, templates) {
  const t = templates || {}
  const s = { ...DEFAULTS, ...(state || {}) }
  const time = `${pad(clamp(s.hour, 0, 23))}:${pad(clamp(s.minute, 0, 59))}`
  const minute = pad(clamp(s.minute, 0, 59))

  switch (s.mode) {
    case MODE_MINUTES:
      return fill(t.minutes, { interval: s.interval })
    case MODE_HOURLY:
      return fill(t.hourly, { minute })
    case MODE_DAILY:
      return fill(t.daily, { time })
    case MODE_WEEKLY:
      return fill(t.weekly, { day: (t.dayNames || [])[clamp(s.weekday, 0, 6)] || s.weekday, time })
    case MODE_MONTHLY:
      return fill(t.monthly, { day: clamp(s.monthday, 1, 31), time })
    default:
      return ""
  }
}

function fill(template, values) {
  if (!template) return ""
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replaceAll(`%{${key}}`, String(value)),
    template
  )
}

function isNumber(value, min, max) {
  if (!/^\d{1,2}$/.test(value)) return false
  const number = Number(value)
  return number >= min && number <= max
}

function clamp(value, min, max) {
  const number = Number(value)
  if (Number.isNaN(number)) return min
  return Math.min(Math.max(Math.trunc(number), min), max)
}

function pad(value) {
  return String(value).padStart(2, "0")
}
